import { Card } from "@/components/ui/card";
import { Scale, TrendingUp, TrendingDown } from "lucide-react";

import {
  getTradeoffSnapshot,
  getBaselineAcceptanceRate,
  getSavingsPer1000,
} from "@/lib/aprTradeoffBaselines";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface APRTradeoffCardProps {
  /** Current discount APR % */
  currentApr: number;
  /** Payment window in days (for savings estimate) */
  paymentWindowDays: number;
  /** Number of invoices that could receive offers (e.g. pending + any pipeline count) */
  pipelineInvoiceCount?: number;
  /** Total $ of pipeline invoices (for estimated savings) */
  pipelineTotalAmount?: number;
  /** Actual accept rate 0–100 from your data (optional, for comparison) */
  yourAcceptRate?: number;
  /** Actual accelerated count (optional) */
  acceleratedCount?: number;
  /** When true, render content in a div instead of Card (e.g. inside a modal) */
  noCard?: boolean;
}

/**
 * Shows the Operator the trade-off when adjusting APR: higher APR = more savings per invoice
 * but typically lower supplier acceptance; lower APR = more acceptance but less savings.
 * Uses baseline data (later can be learned by user/industry).
 */
export function APRTradeoffCard({
  currentApr,
  paymentWindowDays,
  pipelineInvoiceCount = 0,
  pipelineTotalAmount = 0,
  yourAcceptRate,
  acceleratedCount = 0,
  noCard = false,
}: APRTradeoffCardProps) {
  const daysEarly = Math.max(1, paymentWindowDays > 0 ? 30 - paymentWindowDays : 25);
  const current = getTradeoffSnapshot(
    currentApr,
    Math.max(pipelineInvoiceCount, 1),
    Math.max(pipelineTotalAmount, 10000),
    daysEarly
  );

  const lowerApr = Math.max(1, currentApr - 3);
  const higherApr = Math.min(40, currentApr + 3);
  const lowerAccept = getBaselineAcceptanceRate(lowerApr);
  const higherAccept = getBaselineAcceptanceRate(higherApr);
  const lowerSavingsPer10k = getSavingsPer1000(lowerApr, daysEarly) * 10;
  const higherSavingsPer10k = getSavingsPer1000(higherApr, daysEarly) * 10;

  // Calculate optimality score: total estimated savings as % of max possible savings (at ~8% APR optimal)
  const optimalApr = 12;
  const optimalSnapshot = getTradeoffSnapshot(
    optimalApr,
    Math.max(pipelineInvoiceCount, 1),
    Math.max(pipelineTotalAmount, 10000),
    daysEarly
  );
  const savingsRatio =
    optimalSnapshot.estimatedPipelineSavings > 0
      ? current.estimatedPipelineSavings / optimalSnapshot.estimatedPipelineSavings
      : 0;

  // Color based on how close to optimal total savings
  const getOptimalityColor = () => {
    if (savingsRatio >= 0.85)
      return {
        text: "text-success",
        bg: "bg-success/10",
        border: "border-success/30",
        label: "Optimal",
      };
    if (savingsRatio >= 0.6)
      return {
        text: "text-warning",
        bg: "bg-warning/10",
        border: "border-warning/30",
        label: "Moderate",
      };
    return {
      text: "text-destructive",
      bg: "bg-destructive/10",
      border: "border-destructive/30",
      label: "Suboptimal",
    };
  };

  const optColor = getOptimalityColor();

  const content = (
    <>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Scale className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-foreground text-sm tracking-tight">
            APR trade-off
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Higher discount rate = more savings per invoice, but fewer suppliers may accept
          </p>
        </div>
      </div>

      {/* Current APR summary — color-coded by optimality */}
      <div className={`rounded-lg ${optColor.bg} border ${optColor.border} p-3 mb-4`}>
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-bold ${optColor.text} uppercase tracking-wider`}>
            {optColor.label}
          </span>
          <span className={`text-[10px] font-semibold ${optColor.text}`}>
            ~{current.acceptanceRatePct}% acceptance
          </span>
        </div>
        <p className={`text-sm font-display font-bold ${optColor.text}`}>
          At {currentApr}%: {formatCurrency(current.savingsPer10k)} savings per $10K invoice
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5">
          <span className={`text-xs font-semibold ${optColor.text}`}>
            Baseline: ~{current.acceptanceRatePct}% acceptance
            {yourAcceptRate != null && (
              <span className="font-bold"> · Yours: {yourAcceptRate.toFixed(0)}%</span>
            )}
          </span>
          {acceleratedCount > 0 && (
            <span className={`text-[11px] ${optColor.text} opacity-80`}>
              · {acceleratedCount} accelerated so far
            </span>
          )}
        </div>
        {pipelineInvoiceCount > 0 && pipelineTotalAmount > 0 && (
          <p className={`text-[11px] ${optColor.text} opacity-80 mt-1.5`}>
            {pipelineInvoiceCount} pipeline invoice{pipelineInvoiceCount !== 1 ? "s" : ""} (
            {formatCurrency(pipelineTotalAmount)}): ~{current.estimatedInvoicesAccepted} would
            accept → ~{formatCurrency(current.estimatedPipelineSavings)} est. savings
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-muted/50 p-3 border border-border/50">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
            <TrendingDown className="w-3.5 h-3.5" />
            <span>Lower APR ({lowerApr}%)</span>
          </div>
          <p className="text-xs font-semibold text-foreground">
            ~{lowerAccept}% acceptance · {formatCurrency(lowerSavingsPer10k)}/$10K
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            More suppliers likely to accept; less savings per invoice
          </p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 border border-border/50">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Higher APR ({higherApr}%)</span>
          </div>
          <p className="text-xs font-semibold text-foreground">
            ~{higherAccept}% acceptance · {formatCurrency(higherSavingsPer10k)}/$10K
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            More savings per invoice; fewer suppliers may accept
          </p>
        </div>
      </div>
    </>
  );

  if (noCard) {
    return <div className="space-y-4">{content}</div>;
  }

  return <Card className="glass-card p-4 border-border/60">{content}</Card>;
}
